import { useCallback } from 'react';
import sendExperienceAnalytics from '@atlassian/jira-common-experience-tracking-analytics';
import { CHANGE_LAYOUT_EXPERIENCE } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import type {
	GadgetData,
	LayoutType,
	ColumnLayoutType,
} from '@atlassian/jira-dashboard-common/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useDashboardResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';

const expAttributes = {
	analyticsSource: 'dashboard',
	experience: CHANGE_LAYOUT_EXPERIENCE,
	application: null,
	edition: null,
	additionalAttributes: {},
} as const;

export const toColumns = (gadgets: GadgetData[]): ColumnLayoutType => [
	gadgets.filter((gadget) => gadget.column === 0),
	gadgets.filter((gadget) => gadget.column === 1),
	gadgets.filter((gadget) => gadget.column === 2),
];

export const calcColumnsWithNewLayout = (
	currentColumns: ColumnLayoutType,
	currentLayout: string,
	newLayout: string,
) => {
	// if adding a column or no change in column count
	if (currentLayout.length <= newLayout.length) {
		return currentColumns;
	}
	if (currentLayout.length > newLayout.length) {
		const newColumns: ColumnLayoutType = currentColumns.slice(0, newLayout.length);
		const leftOver = currentColumns
			.slice(newLayout.length)
			.flat()
			.map((gadget) => ({ ...gadget, column: newColumns.length - 1 }));
		// append the leftover to last column
		newColumns[newColumns.length - 1] = newColumns[newColumns.length - 1].concat(leftOver);
		return newColumns;
	}

	return currentColumns;
};

export const useLayoutChange = () => {
	const { data, update } = useDashboardResource();

	return useCallback<(arg1: LayoutType, arg2?: ColumnLayoutType) => Promise<void>>(
		async (layout, explicitColumns) => {
			if (!data) {
				return;
			}

			const columns =
				explicitColumns ?? calcColumnsWithNewLayout(toColumns(data.gadgets), data.layout, layout);
			const payload = columns.map((column) => column.map((gadget) => gadget.id));
			const url = `${data.resourceUrl}/layout`;

			try {
				await performPutRequest(url, {
					body: JSON.stringify({
						'0': payload[0],
						'1': payload[1],
						'2': payload[2],
						layout,
					}),
				});

				update(
					(currentData) =>
						currentData && {
							...currentData,
							gadgets: columns.flat(),
							layout,
						},
				);
				sendExperienceAnalytics({
					...expAttributes,
					wasExperienceSuccesful: true,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				sendExperienceAnalytics({
					...expAttributes,
					wasExperienceSuccesful: false,
				});
				fireErrorAnalytics({
					error,
					meta: {
						id: CHANGE_LAYOUT_EXPERIENCE,
						packageName: 'jiraDashboardEdit',
					},
				});
				throw error;
			}
		},
		[data, update],
	);
};
